import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard, GitBranch, Share2, BookOpen, MessageSquare,
  Puzzle, Users, Settings, LogOut, ChevronDown, ChevronLeft, ChevronRight,
} from 'lucide-react';
import { cn } from '../../lib/utils';

const NAV_ITEMS = [
  { icon: LayoutDashboard, label: 'Dashboard',      path: '/dashboard' },
  { icon: GitBranch,       label: 'Repositories',   path: '/repositories' },
  { icon: Share2,          label: 'Architecture',   path: '/architecture' },
  { icon: BookOpen,        label: 'Knowledge Base', path: '/knowledge' },
  { icon: MessageSquare,   label: 'Conversations',  path: '/conversations' },
];

const BOTTOM_NAV = [
  { icon: Puzzle,   label: 'Integrations', path: '/integrations' },
  { icon: Users,    label: 'Team',         path: '/team' },
  { icon: Settings, label: 'Settings',     path: '/settings' },
];

function NavItem({ icon: Icon, label, path, collapsed }: { icon: React.ElementType; label: string; path: string; collapsed: boolean }) {
  const location = useLocation();
  const isActive = location.pathname === path || location.pathname.startsWith(path + '/');

  return (
    <Link
      to={path}
      title={collapsed ? label : undefined}
      className={cn('nav-item group', isActive && 'nav-item-active', collapsed && 'justify-center px-0')}
    >
      <Icon className="w-[15px] h-[15px] flex-shrink-0" />
      {!collapsed && <span>{label}</span>}
      {!collapsed && isActive && <div className="ml-auto w-1 h-1 rounded-full" style={{ backgroundColor: 'var(--t1)' }} />}
    </Link>
  );
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <div className="px-3 mb-1 mt-4 first:mt-0">
      <span className="mono-caps text-[10px] text-t4">
        {children}
      </span>
    </div>
  );
}

export function Sidebar() {
  const [collapsed, setCollapsed] = useState(false);
  const navigate = useNavigate();

  function handleSignOut() {
    localStorage.removeItem('cp_token');
    localStorage.removeItem('cp_user');
    navigate('/');
  }

  const user = (() => {
    try { return JSON.parse(localStorage.getItem('cp_user') || '{}'); } catch { return {}; }
  })();

  const initials = (user.name || 'User')
    .split(' ').slice(0, 2).map((w: string) => w[0]?.toUpperCase() ?? '').join('');

  if (collapsed) {
    return (
      <aside
        className="flex-shrink-0 h-full flex flex-col border-r"
        style={{ width: '52px', backgroundColor: 'var(--s0)', borderColor: 'var(--b-subtle)' }}
      >
        {/* Logo — click to expand */}
        <div className="h-[52px] flex items-center justify-center flex-shrink-0 border-b" style={{ borderColor: 'var(--b-subtle)' }}>
          <button
            onClick={() => setCollapsed(false)}
            title="Expand sidebar"
            className="w-7 h-7 rounded-lg flex items-center justify-center transition-colors"
            onMouseEnter={e => (e.currentTarget.style.backgroundColor = 'var(--overlay-md)')}
            onMouseLeave={e => (e.currentTarget.style.backgroundColor = 'transparent')}
          >
            <img src="/images/codepilotlogo.png" alt="CodePilot" className="w-6 h-6 rounded-md object-contain" />
          </button>
        </div>

        {/* Nav — icons only */}
        <nav className="flex-1 py-3 px-1.5 overflow-y-auto space-y-0.5">
          {NAV_ITEMS.map(item => <NavItem key={item.path} {...item} collapsed />)}
          <div className="my-2 mx-1.5 h-px" style={{ backgroundColor: 'var(--b-subtle)' }} />
          {BOTTOM_NAV.map(item => <NavItem key={item.path} {...item} collapsed />)}
        </nav>

        {/* Bottom — expand button + avatar */}
        <div className="border-t p-2 flex flex-col items-center gap-2" style={{ borderColor: 'var(--b-subtle)' }}>
          <button
            onClick={() => setCollapsed(false)}
            title="Expand sidebar"
            className="w-7 h-7 rounded-md flex items-center justify-center transition-colors"
            style={{ color: 'var(--t4)' }}
            onMouseEnter={e => { e.currentTarget.style.backgroundColor = 'var(--overlay-md)'; e.currentTarget.style.color = 'var(--t2)'; }}
            onMouseLeave={e => { e.currentTarget.style.backgroundColor = 'transparent'; e.currentTarget.style.color = 'var(--t4)'; }}
          >
            <ChevronRight className="w-3.5 h-3.5" />
          </button>
          <div className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: 'var(--t1)' }} title={user.name || 'User'}>
            <span className="text-[11px] font-semibold" style={{ color: 'var(--s0)' }}>{initials || 'U'}</span>
          </div>
        </div>
      </aside>
    );
  }

  return (
    <aside
      className="w-[220px] h-full flex flex-col flex-shrink-0 border-r"
      style={{ backgroundColor: 'var(--s0)', borderColor: 'var(--b-subtle)' }}
    >
      {/* Logo + collapse button */}
      <div className="h-[52px] flex items-center gap-3 px-4 flex-shrink-0 border-b" style={{ borderColor: 'var(--b-subtle)' }}>
        <img src="/images/codepilotlogo.png" alt="CodePilot Engineer" className="w-7 h-7 rounded-lg flex-shrink-0 object-contain" />
        <div className="flex-1 min-w-0">
          <div className="text-[13px] font-semibold text-t1 tracking-tight leading-none">CodePilot</div>
          <div className="text-[10px] text-t4 leading-tight mt-0.5">Engineer</div>
        </div>
        <button
          onClick={() => setCollapsed(true)}
          title="Collapse sidebar"
          className="w-6 h-6 rounded-md flex items-center justify-center flex-shrink-0 transition-colors"
          style={{ color: 'var(--t4)' }}
          onMouseEnter={e => { e.currentTarget.style.backgroundColor = 'var(--overlay-md)'; e.currentTarget.style.color = 'var(--t2)'; }}
          onMouseLeave={e => { e.currentTarget.style.backgroundColor = 'transparent'; e.currentTarget.style.color = 'var(--t4)'; }}
        >
          <ChevronLeft className="w-3.5 h-3.5" />
        </button>
      </div>

      {/* Navigation */}
      <nav className="flex-1 py-3 px-2 overflow-y-auto overflow-x-hidden space-y-0.5">
        <SectionLabel>Platform</SectionLabel>
        {NAV_ITEMS.map(item => <NavItem key={item.path} {...item} collapsed={false} />)}

        <SectionLabel>Configuration</SectionLabel>
        {BOTTOM_NAV.map(item => <NavItem key={item.path} {...item} collapsed={false} />)}
      </nav>

      {/* User section */}
      <div className="border-t p-2" style={{ borderColor: 'var(--b-subtle)' }}>
        <button
          onClick={handleSignOut}
          className="w-full flex items-center gap-2 px-2 py-1.5 rounded-md text-xs text-t4 hover:text-red-400 transition-colors mb-1 group"
          onMouseEnter={e => (e.currentTarget.style.backgroundColor = 'rgba(239,68,68,0.06)')}
          onMouseLeave={e => (e.currentTarget.style.backgroundColor = 'transparent')}
        >
          <LogOut className="w-3.5 h-3.5" />
          Sign out
        </button>

        <div className="flex items-center gap-2.5 px-2 py-2 rounded-lg cursor-pointer transition-colors"
          style={{ backgroundColor: 'transparent' }}
          onMouseEnter={e => (e.currentTarget.style.backgroundColor = 'var(--overlay-sm)')}
          onMouseLeave={e => (e.currentTarget.style.backgroundColor = 'transparent')}
        >
          <div className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: 'var(--t1)' }}>
            <span className="text-[11px] font-semibold" style={{ color: 'var(--s0)' }}>{initials || 'U'}</span>
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[12px] text-t1 truncate leading-none mb-0.5">
              {user.name || 'Engineering Team'}
            </div>
            <div className="text-[10px] text-t4 truncate">
              {user.email || 'Free Plan'}
            </div>
          </div>
          <ChevronDown className="w-3 h-3 text-t4 flex-shrink-0" />
        </div>
      </div>
    </aside>
  );
}
